import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Modal, Select, Switch, Tooltip } from "antd";
import SlotPicker from "./SlotPicker";
import { t } from "i18next";
import { useUser, PERMISSIONS } from "../../core/context/UserContext";
import { fmtDate, fromLocalInput, stateVars, toLocalInput } from "./helpers";

const { TextArea } = Input;

/**
 * "Randevu Bilgileri" kartı — kendi kirli durumu ve kendi Kaydet'i olan bağımsız bir bölüm.
 * Kaydetme yalnızca DEĞİŞEN alanları gönderir; sunucu bunları mevcut randevunun üstüne
 * uygular ve değişmemiş AppointmentService.EditAppointment'a verir.
 */
export default function AppointmentInfoCard({ appointment, lookups, permissions, features, actions, onSave, onStateChange, onCheckIn, onInstitutionChange, saving }) {
    const { hasPermission } = useUser();
    const [form, setForm] = useState({});
    const [dirty, setDirty] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [slotOpen, setSlotOpen] = useState(false);
    // Durum grubunda tanımlı açıklama varsa, durum değişikliği açıklama seçilmeden gitmez.
    const [statePrompt, setStatePrompt] = useState(null); // { stateId, options, descId, description }
    const [stateBusy, setStateBusy] = useState(false);

    const baseline = useMemo(() => ({
        startDate: appointment?.startDate ?? null,
        doctorId: appointment?.doctorId ?? null,
        institutionId: appointment?.institutionId ?? null,
        appointmentTypeId: appointment?.appointmentTypeId ?? null,
        provisionNo: appointment?.provisionNo ?? '',
        note: appointment?.note ?? '',
        anamnez: appointment?.anamnez ?? '',
        privateNote: appointment?.privateNote ?? '',
        sendEnabiz: appointment?.sendEnabiz ?? true,
        enableNotification: appointment?.enableNotification ?? true,
    }), [appointment]);

    // Dış güncelleme (check-in / durum anında kaydedilir, ya da başka randevu seçilir) geldiğinde
    // formu SADECE kirli değilse tazeleriz. Aksi halde kullanıcının o an yazdıkları sessizce
    // silinirdi. Randevu gerçekten değiştiyse kirli olsa da tazelenir — aksi halde bir randevunun
    // verisi başka randevuya yazılırdı.
    const dirtyRef = useRef(false);
    const apIdRef = useRef(null);
    useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

    useEffect(() => {
        const switched = apIdRef.current !== appointment?.id;
        apIdRef.current = appointment?.id;
        if (switched || !dirtyRef.current) {
            setForm(baseline);
            setDirty(false);
        }
    }, [baseline, appointment?.id]);

    if (!appointment) return null;

    const set = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setDirty(true);
    };

    const changedPatch = () => {
        const patch = {};
        Object.keys(baseline).forEach(k => {
            if (form[k] !== baseline[k]) patch[k] = form[k];
        });
        return patch;
    };

    const handleSave = async () => {
        const patch = changedPatch();
        if (Object.keys(patch).length === 0) {
            setDirty(false);
            return;
        }
        const ok = await onSave(patch);
        if (ok) {
            setDirty(false);
            setSavedAt(new Date());
        }
    };

    const handleUndo = () => {
        setForm(baseline);
        setDirty(false);
    };

    const states = appointment.checkIn ? lookups.statesAfterCheckIn : lookups.statesBeforeCheckIn;
    const readOnly = !permissions.canCreateEdit;

    const requestStateChange = (stateId) => {
        const target = (states || []).find(s => s.id === stateId);
        const options = (lookups.stateDescriptions || [])
            .filter(d => d.stateGroupId === target?.stateGroupId);

        if (options.length === 0) {
            onStateChange({ stateId });
            return;
        }
        setStatePrompt({ stateId, options, descId: options[0].id, description: '' });
    };

    // Açıklama zorunlu olan durumlarda onay, isteğin sonucunu bekler: erken kapanınca kullanıcı
     // işlemin gerçekten geçtiğini göremiyordu ve düğmeye ikinci kez basılabiliyordu.
    const confirmStateChange = async () => {
        if (!statePrompt || stateBusy) return;
        setStateBusy(true);
        try {
            await onStateChange({
                stateId: statePrompt.stateId,
                descId: statePrompt.descId,
                description: statePrompt.description || null,
            });
        } finally {
            setStateBusy(false);
            setStatePrompt(null);
        }
    };

    return (
        <section className={`card${dirty ? ' dirty' : ''}`} style={{ '--ws-ca': 'var(--ws-accent)' }}>
            <Modal
                open={slotOpen}
                onCancel={() => setSlotOpen(false)}
                rootClassName="ws-root"
                title={`${t('Select Appointment Time')} — ${appointment.unitName ?? ''}`}
                width="min(900px, 96vw)"
                style={{ top: 20 }}
                footer={<Button onClick={() => setSlotOpen(false)}>{t('Close')}</Button>}
                destroyOnClose
            >
                <SlotPicker
                    unitId={appointment.unitId}
                    unitName={appointment.unitName}
                    date={form.startDate || appointment.startDate}
                    excludeAppointmentId={appointment.id}
                    onSelect={({ start }) => {
                        set('startDate', start);
                        setSlotOpen(false);
                    }}
                />
            </Modal>

            <Modal
                open={!!statePrompt}
                onCancel={() => !stateBusy && setStatePrompt(null)}
                onOk={confirmStateChange}
                confirmLoading={stateBusy}
                maskClosable={!stateBusy}
                rootClassName="ws-root"
                title={t('State Description')}
                okText={t('Apply')}
                cancelText={t('Cancel')}
                destroyOnClose
            >
                <div className="grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div className="f">
                        <span className="lbl">{t('Reason')}</span>
                        <Select
                            value={statePrompt?.descId}
                            onChange={v => setStatePrompt(prev => ({ ...prev, descId: v }))}
                            options={(statePrompt?.options || []).map(o => ({ value: o.id, label: o.text }))}
                        />
                    </div>
                    <div className="f">
                        <span className="lbl">{t('Description')}</span>
                        <TextArea
                            rows={3}
                            value={statePrompt?.description ?? ''}
                            onChange={e => setStatePrompt(prev => ({ ...prev, description: e.target.value }))}
                        />
                    </div>
                </div>
            </Modal>
            <div className="cardhead">
                <h3>{t('Appointment')} {t('Details')}</h3>
                <span className="sp" />
            </div>

            <div className="cardbody">
                <div className="grid">
                    <div className="f c2">
                        <span className="lbl">
                            {t('Start Date')}
                            <Button
                                size="small"
                                type="link"
                                style={{ padding: 0, height: 'auto', fontSize: 11 }}
                                disabled={readOnly}
                                onClick={() => setSlotOpen(true)}
                            >
                                {t('Pick from calendar')}
                            </Button>
                        </span>
                        <Input
                            type="datetime-local"
                            className="ws-mono"
                            disabled={readOnly}
                            value={toLocalInput(form.startDate)}
                            onChange={e => set('startDate', fromLocalInput(e.target.value))}
                        />
                    </div>
                    <div className="f c2">
                        <span className="lbl">
                            {t('End Date')} <span className="hint">· {t('from operation duration')}</span>
                        </span>
                        <Input readOnly className="ws-mono" value={fmtDate(appointment.endDate)} />
                    </div>

                    <div className="f c2">
                        <span className="lbl">{t('Doctor')}</span>
                        <Select
                            disabled={readOnly}
                            value={form.doctorId ?? undefined}
                            onChange={v => set('doctorId', v)}
                            options={(lookups.doctors || []).map(d => ({ value: d.id, label: d.name }))}
                        />
                    </div>
                    <div className="f c2">
                        <span className="lbl">{t('Institution')}</span>
                        <Select
                            /* Kurum geçmiş randevuda kilitli: fiyatlar kuruma bağlı çözülüyor,
                               geçmişe dönük değiştirmek tutarları değiştirirdi. */
                            disabled={readOnly || permissions.pastLocked}
                            showSearch
                            optionFilterProp="label"
                            value={form.institutionId ?? undefined}
                            onChange={v => {
                                set('institutionId', v);
                                onInstitutionChange?.(v);
                            }}
                            options={(lookups.institutions || []).map(i => ({ value: i.id, label: i.name }))}
                        />
                        <span className="hint">{t('Prices are resolved on the server when the institution changes.')}</span>
                    </div>

                    <div className="f c2">
                        <span className="lbl">{t('Appointment Type')}</span>
                        <Select
                            disabled={readOnly}
                            value={form.appointmentTypeId ?? undefined}
                            onChange={v => set('appointmentTypeId', v)}
                            options={(lookups.appointmentTypes || []).map(x => ({ value: x.id, label: x.name }))}
                        />
                    </div>
                    <div className="f c2">
                        <span className="lbl">{t('Provision No')}</span>
                        <Input
                            className="ws-mono"
                            disabled={readOnly}
                            value={form.provisionNo ?? ''}
                            onChange={e => set('provisionNo', e.target.value)}
                        />
                    </div>

                    <div className="f c2">
                        <span className="lbl">Anamnez</span>
                        <TextArea
                            rows={3}
                            disabled={readOnly}
                            value={form.anamnez ?? ''}
                            onChange={e => set('anamnez', e.target.value)}
                        />
                    </div>
                    <div className="f c2">
                        <span className="lbl">{t('Appointment Note')}</span>
                        <TextArea
                            rows={3}
                            disabled={readOnly}
                            value={form.note ?? ''}
                            onChange={e => set('note', e.target.value)}
                        />
                    </div>

                    {hasPermission(PERMISSIONS.VIEW_PRICE) && (
                        <div className="f c4">
                            <span className="lbl">{t('Private Note')}</span>
                            <Input
                                disabled={readOnly}
                                value={form.privateNote ?? ''}
                                onChange={e => set('privateNote', e.target.value)}
                            />
                        </div>
                    )}

                    <div className="c4">
                        <div className="switches">
                            {/* Check-in ve durum anında kaydedilir: en sık tekrarlanan iş,
                                ayrıca "Kaydet" beklemesin. */}
                            <span className="swbox">
                                <strong>{t('Check-in')}</strong>
                                <Switch
                                    size="small"
                                    disabled={readOnly}
                                    checked={appointment.checkIn}
                                    onChange={onCheckIn}
                                />
                            </span>
                            <span className="swbox" style={stateVars(appointment.stateColor)}>
                                <span className="lbl">{t('State')}</span>
                                <Select
                                    disabled={readOnly}
                                    style={{ minWidth: 180 }}
                                    value={appointment.stateId}
                                    open={statePrompt ? false : undefined}
                                    onChange={requestStateChange}
                                    options={(states || []).map(s => ({
                                        value: s.id,
                                        label: (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{
                                                    width: 12, height: 12, borderRadius: 2,
                                                    background: s.color, border: '1px solid rgba(0,0,0,.3)',
                                                    display: 'inline-block',
                                                }} />
                                                {s.name}
                                            </span>
                                        ),
                                    }))}
                                />
                            </span>
                            <span className="sp" style={{ flex: 1 }} />
                            <span className={`swbox${!appointment.unitEnabizEnabled ? ' off' : ''}`}>
                                <Tooltip title={t('When off, this appointment is excluded from automatic E-Nabız sending.')}>
                                    <strong>{t('Send E-Nabız')}</strong>
                                </Tooltip>
                                <Switch
                                    size="small"
                                    disabled={readOnly || !appointment.unitEnabizEnabled}
                                    checked={appointment.unitEnabizEnabled && form.sendEnabiz !== false}
                                    onChange={v => set('sendEnabiz', v)}
                                />
                                {/* Uyarı anahtarın yanında duruyor: kartın dibinde dururken
                                    neyi anlattığı görünmüyordu. */}
                                {!appointment.unitEnabizEnabled && (
                                    <Tooltip title={t('E-Nabız sending appears to be disabled for the institution / unit / appointment type. Please inform IT before sending.')}>
                                        <span className="warnmark">!</span>
                                    </Tooltip>
                                )}
                            </span>
                            <span className="swbox">
                                <Tooltip title={t('When off, no notifications are produced/sent for this appointment.')}>
                                    <strong>{t('Send Notifications')}</strong>
                                </Tooltip>
                                <Switch
                                    size="small"
                                    disabled={readOnly}
                                    checked={form.enableNotification !== false}
                                    onChange={v => set('enableNotification', v)}
                                />
                            </span>
                        </div>
                    </div>
                </div>

                {/* Kurum tanımındaki not (institution.note). Başlıksız basılınca metnin nereden
                    geldiği anlaşılmıyordu; hangi kuruma ait olduğu artık yazıyor. */}
                {appointment.institutionNote && (
                    <div className="instnote">
                        <span className="lbl">
                            {t('Institution note')}
                            {appointment.institutionName ? ` · ${appointment.institutionName}` : ''}
                        </span>
                        <div dangerouslySetInnerHTML={{ __html: appointment.institutionNote }} />
                    </div>
                )}

            </div>

            <div className={`cardfoot${dirty ? ' dirty' : ''}`}>
                <span className="state">
                    <span className="dot" />
                    <span>
                        {dirty
                            ? t('Unsaved changes')
                            : savedAt ? `${t('Saved')} · ${savedAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}` : t('Saved')}
                    </span>
                </span>
                <span className="sp" />
                <Button size="small" disabled={!dirty} onClick={handleUndo}>{t('Undo')}</Button>
                <Button
                    size="small"
                    type="primary"
                    disabled={!dirty || readOnly}
                    loading={saving === 'info'}
                    onClick={handleSave}
                >
                    {t('Save Details')}
                </Button>
            </div>
        </section>
    );
}
